import React, { useEffect, useRef, useCallback } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { renderText, useChatContext, type MessageContextValue } from 'stream-chat-react'
import RichTextMessagingInput from '../RichTextInput/RichTextMessagingInput'
import type { DefaultStreamChatGenerics } from '../../types'

/* ============================================================================
   Types
============================================================================ */
interface EditMessageFormProps<
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> {
  show: boolean
  message: MessageContextValue<StreamChatGenerics>['message']
  clearEditingState: () => void
}

/* ============================================================================
   Constants
============================================================================ */
const WRAP_ID = 'edit-message-form'

// Removed legacy toolbar classes after migrating to RichTextMessagingInput

/* ============================================================================
   Helper: set value on a controlled textarea the "React-safe" way
   (use the native property setter and dispatch an input event so React state updates)
============================================================================ */
// Legacy helper kept for reference; no longer used after migration

/* ============================================================================
   Robust HTML → Markdown Conversion (Stream renders markdown, not HTML)
============================================================================ */
function htmlToMarkdown(html: string): string {
  const root = document.createElement('div')
  root.innerHTML = html

  // Treat common block containers as newline boundaries
  const blockSelectors = ['p', 'div', 'section', 'article', 'header', 'footer']
  root.querySelectorAll(blockSelectors.join(',')).forEach((el) => {
    if (!el.textContent) return
    el.insertAdjacentText('afterend', '\n')
  })

  // br -> newline
  root.querySelectorAll('br').forEach((br) => (br.outerHTML = '\n'))

  // strong/bold
  root.querySelectorAll('strong, b').forEach((el) => {
    const t = el.textContent ?? ''
    el.outerHTML = `**${t}**`
  })

  // italic/em
  root.querySelectorAll('em, i').forEach((el) => {
    const t = el.textContent ?? ''
    el.outerHTML = `*${t}*`
  })

  // strike
  root.querySelectorAll('s, strike, del').forEach((el) => {
    const t = el.textContent ?? ''
    el.outerHTML = `~~${t}~~`
  })

  // spans with inline styles we care about
  root.querySelectorAll('span[style]').forEach((span) => {
    const style = span.getAttribute('style')?.toLowerCase() || ''
    const t = span.textContent ?? ''
    if (/font-weight\s*:\s*(bold|600|700|800|900)/.test(style)) {
      span.outerHTML = `**${t}**`
    } else if (/font-style\s*:\s*italic/.test(style)) {
      span.outerHTML = `*${t}*`
    } else if (/text-decoration\s*:\s*line-through/.test(style)) {
      span.outerHTML = `~~${t}~~`
    }
  })

  // links
  root.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? ''
    const text = (a.textContent ?? href).trim()
    a.outerHTML = `[${text}](${href})`
  })

  // unordered lists
  root.querySelectorAll('ul').forEach((ul) => {
    const items = Array.from(ul.querySelectorAll(':scope > li'))
    const md = items.map((li) => `- ${li.textContent?.trim() ?? ''}`).join('\n')
    ul.outerHTML = `${md}\n`
  })

  // ordered lists
  root.querySelectorAll('ol').forEach((ol) => {
    const items = Array.from(ol.querySelectorAll(':scope > li'))
    const md = items.map((li, i) => `${i + 1}. ${li.textContent?.trim() ?? ''}`).join('\n')
    ol.outerHTML = `${md}\n`
  })

  // Plain text cleanup
  const text = root.textContent ?? ''
  return text
    .replace(/\u00a0/g, ' ') // nbsp -> space
    .replace(/[ \t]+\n/g, '\n') // strip trailing spaces
    .replace(/\n{3,}/g, '\n\n') // collapse >2 newlines
    .trim()
}

/* ============================================================================
   Toolbar Actions (contenteditable) – using execCommand for broad support
============================================================================ */
// Legacy toolbar helpers removed

// Legacy toolbar helpers removed

/* ============================================================================
   Component
============================================================================ */
export const EditMessageForm = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>({
  show,
  message,
  clearEditingState,
}: EditMessageFormProps<StreamChatGenerics>) => {
  /* ------------------------------ Refs & State ---------------------------- */
  const editorRef = useRef<HTMLDivElement | null>(null)
  const { client } = useChatContext<StreamChatGenerics>()

  // pressed state for inline formats based on selection
  // Legacy formatting state removed

  /* --------------------------- Selection tracking ------------------------- */
  const refreshActiveFromSelection = useCallback(() => {}, [])

  useEffect(() => {
    if (!show) return
    refreshActiveFromSelection()
    document.addEventListener('selectionchange', refreshActiveFromSelection)
    return () => document.removeEventListener('selectionchange', refreshActiveFromSelection)
  }, [show, refreshActiveFromSelection])

  /* ---------------------- Initialize editor content ----------------------- */
  useEffect(() => {
    if (!show || !editorRef.current) return

    const rendered = renderText(message?.text ?? '')

    let html = ''
    if (typeof rendered === 'string') {
      html = rendered
    } else if (React.isValidElement(rendered)) {
      html = renderToStaticMarkup(rendered)
    } else {
      html = String(rendered ?? '')
    }

    editorRef.current.innerHTML = html
  }, [show, message?.text])

  /* ------------------------------ DOM Helpers ----------------------------- */
  // Legacy DOM helper removed

  /* ------------------------------ Toolbar Model --------------------------- */
  // Legacy Tool type removed

  // Legacy inline toggle removed

  // Legacy emoji trigger removed

  // Legacy attach trigger removed

  // Removed legacy toolbar after migrating to RichTextMessagingInput

  /* -------------------------------- Handlers ------------------------------ */
  // Removed legacy save handler; using direct update through RichTextMessagingInput

  const handleSaveWithDirectUpdate = useCallback(async () => {
    try {
      const html = editorRef.current?.innerHTML ?? ''
      const md = htmlToMarkdown(html)
      if (!client || !message?.id) return
      // Narrowly type the call to avoid any
      const updater: (payload: { id: string; text: string }) => Promise<unknown> =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (client as unknown as { updateMessage: (payload: { id: string; text: string }) => Promise<unknown> }).updateMessage
      await updater({ id: message.id, text: md })
      clearEditingState()
    } catch (e) {
      console.error('Failed to update message:', e)
    }
  }, [client, message?.id, clearEditingState])

  /* --------------------------- Conditional Render ------------------------- */
  if (!show) return null

  /* --------------------------------- Render ------------------------------- */
  return (
    <div className="mt-2 w-full">
      <div className="flex gap-3 justify-end">
        <div className="flex flex-col max-w-[75%] items-end">
          <div className="w-full bg-white rounded-2xl border border-app-grey-25 shadow-sm overflow-hidden md:min-w-[600px] xs:min-w-full">
            {/* Header */}
            <div className="px-4 py-3 border-b border-app-grey-25 bg-app-grey-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-app-grey-60"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  <span className="text-sm font-medium text-app-grey-80">Editing message</span>
                </div>
                <button
                  onClick={clearEditingState}
                  className="p-1 rounded hover:bg-app-grey-20 transition-colors"
                  aria-label="Close editing"
                >
                  <svg
                    className="w-4 h-4 text-app-grey-60"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Use RichTextMessagingInput for editing */}
              <RichTextMessagingInput
                initialContent={(message?.html as string) || message?.text || ''}
                onSubmitOverride={handleSaveWithDirectUpdate}
              />

              {/* Hidden Stream MessageInput kept for fallback submission path */}
             

              {/* Actions */}
              
            </div>
          </div>
        </div>
      </div>

      {/* Hide Stream's default controls inside the hidden MessageInput */}
      <style>{`
        #${WRAP_ID} [data-testid="emoji-picker-button"],
        #${WRAP_ID} [data-testid="attach-button"],
        #${WRAP_ID} .str-chat__message-textarea-emoji-picker-button,
        #${WRAP_ID} .str-chat__message-textarea-attach-button,
        #${WRAP_ID} .str-chat__send-button,
        #${WRAP_ID} button[type="submit"] {
          display: none !important;
        }
      `}</style>
    </div>
  )
}
